import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "preview-canvas-container" }
const _hoisted_2 = { class: "text-render-container" }

import {
  computed,
  onMounted,
  ref,
  watch
} from 'vue'
import * as PIXI from 'pixi.js'
import useEffect from '@/composables/UseEffect'
import { useProjectStore } from '@/store/project-store'
import { ActionMenuTabs, useApplicationStore } from '@/store/application-store'
import useTextStates from '@/composables/UseTextStates'
import useTextCanvas, { TextImplementation } from '@/composables/UseTextCanvas'
import { MinimumScaleType } from '@/pixi/Transformer'
import { Layers } from '@/static/Layers'
import { DisplayObjectPair } from '@/pixi/types'
import EditTextOverlay from '@/components/preview/EditTextOverlay.vue'
import useLayers from '@/composables/UseLayers'
import { useBackgroundCanvas, BackgroundCanvasState } from '@/composables/UseBackgroundCanvas'
import useStickerCanvas from '@/composables/UseStickerCanvas'
import useTransformer, { TransformerHandleDataInterface, MouseFlagsInterface } from '@/composables/UseTransformer'
import useDisplayObject from '@/composables/UseDisplayObject'
import { isMouseOnElement } from  '@/utils/mouse-helper'
import CanvasControls from '@/components/preview/CanvasControls.vue'
import useCanvasEventHandler from '@/composables/UseCanvasEventHandler'


export default /*#__PURE__*/_defineComponent({
  __name: 'PreviewCanvas',
  props: {
    showCanvasControls: { type: Boolean }
  },
  emits: ["initPixiApplication"],
  setup(__props: any, { emit: emits }: { emit: ({
  (event: 'initPixiApplication', pixiApplication: PIXI.Application): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  showCanvasControls: boolean
};

const applicationStore = useApplicationStore()
const projectStore = useProjectStore()

const { canvasLayers, transformerLayer } = useLayers()
const { updateStickers } = useStickerCanvas()
const { moveElementOnCanvasResize, updateDisplayObjState, onTransformDisplayObject } = useDisplayObject()
const { createTransformer } = useTransformer()
const { getLayerZIndex } = useLayers()
const { addBackground, scaleBackground } = useBackgroundCanvas()
const { createTextImplementation, rasterizeText } = useTextCanvas()
const { editText } = useTextStates()
const { handleUserKeyPress, handleCopy, handlePaste, handleCut } = useCanvasEventHandler()
const { applyEffectsToLayers } = useEffect()

// These store PIXI display objects. The key is the ID of the display object state.
const textDisplayObjectsMap = new Map<number, DisplayObjectPair>()
const stickerDisplayObjectsMap = new Map<number, DisplayObjectPair>()
const backgroundCanvasState : BackgroundCanvasState = {
  url: '',
  scale: 'fill',
  sprite: null
}

PIXI.utils.skipHello()
const canvas = ref<HTMLCanvasElement | null>(null)

const mouseFlags: MouseFlagsInterface = {
  isMouseDown: false
}
const transformerHandleData : TransformerHandleDataInterface = {
  isTransformerHandleGrabbed: false,
  selectedHandle: null
}




// This array contains implementation details associated with text states
const textImplementations = ref<TextImplementation[]>([])

const showTextEditOverlay = computed(() => applicationStore.activeActionTab === ActionMenuTabs.TEXT && applicationStore.editTextState !== 'none')
const selectedTextImplementation = computed(() => textImplementations.value.find((textImplementation) => applicationStore.selectedText?.id === textImplementation.state.id))

// This watcher watches the text state array change so that we can update the implementation data along with it
// The reason why we don't use a `computed` property here is because we don't want to lose previous instances of
// the implementation objects which hold their own state (e.g. references to DOM objects).
// NOTE: This implementation is a bit... janky. Not sure if this is the right way to go when we have data in the store.
watch(() => projectStore.textStates, () => {
  const oldTextImplementations = [...textImplementations.value]
  const newTextImplementations = []

  for (const textState of projectStore.textStates) {
    const existingTextImplementation = oldTextImplementations.find(
      textImplementation => textImplementation.state === textState
    )

    // Reuse existing text implementation objects if the corresponding text state still exists, otherwise create
    // a new text implementation object
    if (existingTextImplementation) {
      newTextImplementations.push(existingTextImplementation)
    } else {
      newTextImplementations.push(createTextImplementation(textState))
    }
  }

  textImplementations.value = newTextImplementations
}, {
  immediate: true,
  deep: true
})

onMounted(() => {
  if (canvas.value) {
    window.addEventListener('keydown', handleUserKeyPress)
    window.addEventListener('copy', handleCopy)
    window.addEventListener('cut', handleCut)
    window.addEventListener('paste', handlePaste)

    const application = new PIXI.Application({
      view: canvas.value,
      backgroundColor: 0x000000,
      sharedTicker: true
    })
    emits('initPixiApplication', application)
    const backgroundLoader = new PIXI.Loader(
      undefined, // Base URL
      1 // Concurrency
    )

    // Allow containers to be automatically sorted by their zIndex property
    application.stage.sortableChildren = true

    application.stage.addChild(canvasLayers[Layers.BACKGROUND], canvasLayers[Layers.TEXT],
      canvasLayers[Layers.STICKER], canvasLayers[Layers.LOGO], transformerLayer)

    // This watcher watches the text implementations array changing - when text is added or removed we add or remove the
    // corresponding PIXI display objects
    watch(textImplementations, () => {
      // Delete any removed text
      for (const [id, { sprite, transformer }] of textDisplayObjectsMap.entries()) {
        if (!textImplementations.value.some((textImplementation) => textImplementation.state.id === id)) {
          canvasLayers[Layers.TEXT].removeChild(sprite)
          sprite.destroy()
          transformerLayer.removeChild(transformer)
          transformer.destroy(true)
          textDisplayObjectsMap.delete(id)
        }
      }
      for (const [index, textImplementation] of textImplementations.value.entries()) {
        const textDisplayObjects = textDisplayObjectsMap.get(textImplementation.state.id)

        // If display objects already exist for this implementation, ensure the correct z ordering
        if (textDisplayObjects) {
          const { sprite, transformer } = textDisplayObjects
          canvasLayers[Layers.TEXT].setChildIndex(sprite, index)
          transformer.zIndex = getLayerZIndex(Layers.TEXT, index, textImplementations.value.length)
        }
        else {
          // Create text display objects as it does not exist for this implementation
          const textSprite = new PIXI.Sprite()
          textSprite.anchor.set(0.5)
          // Add transformation to the text
          const transformer = createTransformer(
            [textSprite],
            MinimumScaleType.RELATIVE,
            application,
            transformerHandleData,
            mouseFlags,
            textDisplayObjectsMap,
            stickerDisplayObjectsMap
          )

          // This watcher toggles the visibility of the bounding box and scale handles
          watch(() => applicationStore.selectedText, () => {
            transformer.enabledHandles = applicationStore.selectedText === textImplementation.state ? ['rotator', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight'] : []
            transformer.boundingBoxes = applicationStore.selectedText === textImplementation.state ? 'all' : 'none'
            transformer.rotateEnabled = applicationStore.selectedText === textImplementation.state
          }, { immediate: true })

          // Hand rolled double tap/click handler - we use this for editing mode (for now)
          let lastPointerTapTime = performance.now()
          transformer.addListener('pointertap', (e: PIXI.InteractionEvent) => {
            // Prevent secondary (right) click
            if (e.data.button === 2) {
              return
            }
            const now = performance.now()
            const timeSinceLastTap = now - lastPointerTapTime
            if (timeSinceLastTap < 200) {
              // This is a double tap
              editText()
            }
            lastPointerTapTime = now
            mouseFlags.isMouseDown = false
          })

          transformer.addListener('pointerdown', (e: PIXI.InteractionEvent) => {
            // Prevent secondary (right) click
            if (e.data.button === 2) {
              return
            }

            applicationStore.selectText(textImplementation.state)
            mouseFlags.isMouseDown = true
          })

          transformer.addListener('mouseover', () => {
            if (applicationStore.selectedText !== textImplementation.state && !mouseFlags.isMouseDown) {
              transformer.boundingBoxes = 'all'
            }
          })

          transformer.addListener('mouseout', () => {
            if (applicationStore.selectedText !== textImplementation.state) {
              transformer.boundingBoxes = 'none'
            }
          })

          transformer.addListener('transformchange', () => {
            onTransformDisplayObject(
              textImplementation.state,
              textDisplayObjectsMap,
              stickerDisplayObjectsMap,
              transformerLayer,
              transformerHandleData.isTransformerHandleGrabbed,
              projectStore.resolution
            )
          })

          canvasLayers[Layers.TEXT].addChildAt(textSprite, index)
          transformer.zIndex = getLayerZIndex(Layers.TEXT, index, textImplementations.value.length)
          transformerLayer.addChild(transformer)

          textDisplayObjectsMap.set(textImplementation.state.id, { sprite: textSprite, transformer })

          // This watcher watches if editing mode is being toggled
          watch(() => applicationStore.editTextState, async () => {
            if (textImplementation.renderTextReferenceElement && applicationStore.currentEditingText === textImplementation.state) {
              if (applicationStore.editTextState === 'editing') {
                textImplementation.isTextRasterised = false
                // Hide PIXI objects
                textSprite.visible = false
                transformer.visible = false
              } else if (applicationStore.editTextState === 'none') {
                // If editing mode is disabled, we need to rasterise the text into an image
                try {
                  await rasterizeText(textImplementation, textDisplayObjectsMap)
                } catch {
                  applicationStore.pushSnackbarMessage({
                    header: 'Oops something went wrong',
                    text: 'Text conversion failed',
                    state: 'error'
                  })
                }
                // Show PIXI objects
                textSprite.visible = true
                transformer.visible = true
                textImplementation.isTextRasterised = true
              }
            }
          }, {
            immediate: true
          })

          // This watcher watches text style changing
          watch(textImplementation.state.style, async () => {
            if (applicationStore.editTextState !== 'editing' && textImplementation.isTextRasterised) {
              // Re-rasterize the text if style has changed outside of edit mode
              try {
                await rasterizeText(textImplementation, textDisplayObjectsMap)
              } catch {
                applicationStore.pushSnackbarMessage({
                  header: 'Oops something went wrong',
                  text: 'Text conversion failed',
                  state: 'error'
                })
              }
            }
          }, {
            // We want this watcher to run _after_ the DOM has been updated, so that html2canvas has the correct
            // properties before rasterising
            flush: 'post'
          })
        }
      }
    }, {
      immediate: true,
      flush: 'post'
    })

    let oldCanvasWidth = projectStore.resolution.width
    let oldCanvasHeight = projectStore.resolution.height

    watch(() => projectStore.resolution, () => {
      const { width, height } = projectStore.resolution
      application.renderer.resize(width, height)
      canvasLayers.forEach(layer => {
        layer.filterArea.width = width
        layer.filterArea.height = height
      })

      backgroundCanvasState.scale = scaleBackground(application, backgroundCanvasState.sprite)
      stickerDisplayObjectsMap.forEach((stickerObjPair, id) => {
        moveElementOnCanvasResize(stickerObjPair.transformer, oldCanvasWidth, oldCanvasHeight, application)
        const stickerState = projectStore.stickers.concat(projectStore.logoStickers).find((sticker) => sticker.id === id)
        if (stickerState) {
          updateDisplayObjState(stickerState, textDisplayObjectsMap, stickerDisplayObjectsMap)
        }
      })

      textDisplayObjectsMap.forEach((textObjPair, id) => {
        moveElementOnCanvasResize(textObjPair.transformer, oldCanvasWidth, oldCanvasHeight, application)
        // scale text sprite down and into view if too big for resolution
        if (textObjPair.sprite.width > width) {
          textObjPair.sprite.scale.set(textObjPair.sprite.scale.x * width / textObjPair.sprite.width)
        }
        const textImplementation = textImplementations.value.find((textImp) => textImp.state.id === id)
        if (textImplementation) {
          updateDisplayObjState(textImplementation.state, textDisplayObjectsMap, stickerDisplayObjectsMap)
        }
      })

      // Keep track of canvas dimensions before resizing
      oldCanvasWidth = width
      oldCanvasHeight = height
    }, {
      immediate: true
    })
    watch(() => projectStore.background.backgroundUrl, (newBackgroundUrl) => {

      if (newBackgroundUrl !== backgroundCanvasState.url) {
        addBackground(
          application,
          backgroundLoader,
          canvasLayers[Layers.BACKGROUND],
          backgroundCanvasState
        )
      }
    }, {
      immediate: true
    })

    watch(() => projectStore.background.backgroundScale, (newBackgroundScale) => {
      // Scale is applied at the end of background loading
      if (!applicationStore.isBackgroundLoading) {
        if (newBackgroundScale !== backgroundCanvasState.scale) {
          backgroundCanvasState.scale = scaleBackground(application, backgroundCanvasState.sprite)
        }
      }
    })

    // This watcher watches the stickers and deletes/loads them from/onto the canvas
    watch(() => projectStore.stickers, () => {
      updateStickers(
        application,
        projectStore.stickers,
        canvasLayers[Layers.STICKER],
        transformerLayer,
        transformerHandleData,
        mouseFlags,
        stickerDisplayObjectsMap,
        textDisplayObjectsMap
      )
    }, {
      immediate: true,
      deep: true
    })

    // This watcher watches the logos and deletes/loads them from/onto the canvas
    watch(() => projectStore.logoStickers, () => {
      updateStickers(
        application,
        projectStore.logoStickers,
        canvasLayers[Layers.LOGO],
        transformerLayer,
        transformerHandleData,
        mouseFlags,
        stickerDisplayObjectsMap,
        textDisplayObjectsMap
      )
    }, {
      immediate: true,
      deep: true
    })
  }

  // get the preview area HTML element
  const previewArea = document.getElementsByClassName('preview-area')[0]
  // track the coordinates of the mouse to disable the effects when the cursor is above the canvas
  previewArea.addEventListener('mousemove', (event) => {
    // don't proceed if there is no active effects (there is nothing to disable)
    if (projectStore.activeEffectStates.size === 0) {
      return
    }

    const isMouseOn = isMouseOnElement(canvas.value, event)
    if (isMouseOn && !applicationStore.disableEffectsOnPreview) {
      // disable active filters
      applicationStore.disableEffectsOnPreview = true
      canvasLayers.forEach(layer => {
        layer.updateEffects([]) //pause effects
      })
    } else if (!isMouseOn && applicationStore.disableEffectsOnPreview) {
      // enable active filters
      applicationStore.disableEffectsOnPreview = false
      applyEffectsToLayers()
    }
  })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("canvas", {
      class: "preview-canvas",
      ref_key: "canvas",
      ref: canvas,
      id: "canvas"
    }, null, 512),
    _createVNode(EditTextOverlay, {
      class: "preview-canvas__edit-text-overlay",
      "show-overlay": _unref(showTextEditOverlay)
    }, null, 8, ["show-overlay"]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(textImplementations.value, (textImplementation) => {
      return (_openBlock(), _createBlock(_Teleport, {
        key: textImplementation.state.id,
        to: "body"
      }, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", {
            ref_for: true,
            ref: el => textImplementation.setRenderTextReferenceElement(el),
            class: "preview-text-area",
            style: _normalizeStyle(textImplementation.state.cssStyle)
          }, _toDisplayString(textImplementation.state.text), 5)
        ])
      ]))
    }), 128)),
    _withDirectives(_createVNode(CanvasControls, {
      class: "preview-canvas__canvas-controls",
      "background-state": backgroundCanvasState,
      "sticker-display-objects-map": _unref(stickerDisplayObjectsMap),
      "text-display-objects-map": _unref(textDisplayObjectsMap),
      "selected-text-implementation": _unref(selectedTextImplementation)
    }, null, 8, ["sticker-display-objects-map", "text-display-objects-map", "selected-text-implementation"]), [
      [_vShow, __props.showCanvasControls]
    ])
  ]))
}
}

})